<template>
    <!-- [info]: this h-full could cause some issue. if it does remove it -->
    <div
        v-show="isEditorReady"
        class="h-full"
        :class="`${isDisabled ? 'pointer-events-none select-none opacity-60' : ''}`"
    >
        <textarea ref="joditEditor" id="editor"></textarea>
    </div>
    <div v-if="!isEditorReady">
        <span class="inline-flex w-full h-10 bg-gray-200 opacity-20 rounded-lg skeleton-animation"></span>
    </div>

    <!-- [info]: this is just a temp input using it for unblur the jodit editor -->
    <input class="opacity-0 fixed top-[-90000px] -z-50" ref="tempInput" />
</template>
<script setup>
import { Jodit } from "jodit-pro/es2021/jodit";
import "../../../../joditConfig";
import { ref, onMounted, computed, watch, defineExpose, nextTick, onUnmounted } from "vue";

let debounceSelectionHandler = null;

const props = defineProps({
    modelValue: { type: String, required: true, default: "" },
    buttons: { type: Array, default: null },
    extraButtons: { type: Array, default: null },
    config: { type: Object, default: () => ({}) },
    plugins: { type: Array, default: () => [] },
    isDisabled: { type: Boolean, default: false },
    selectionEmitEnabled: { type: Boolean, default: false },
});

const emits = defineEmits(["update:modelValue", "handleShiftEnterKey", "hasSelected", "joditIsReady"]);

const joditEditor = ref();
const isEditorReady = ref(false);

// [info]: ELEM
const tempInput = ref(null);
const editorConfig = computed(() => {
    const config = { ...props.config };
    if (props.buttons) {
        config.buttons = props.buttons;
    }
    config.buttonsMD = props.buttons;
    config.buttonsSM = props.buttons;
    config.buttonsXS = props.buttons;
    return config;
});

// [info]: methods
const handleStyles = (style) => {
    if (!style) return;

    if (style === "blockquote") jodit.execCommand("formatBlock", false, "blockquote");
    else if (style === "ai-assistant") jodit.e.fire("generateAiAssistantForm.ai-assistant", "ai");
    else jodit.execCommand(style);
};

function getJoditSelectionStyles(joditInstance = jodit) {
    const s = joditInstance.s; // Jodit selection module [2, 4, 5]

    // Ensure selection module and a valid browser selection/range exist
    if (!s || !s.sel || s.sel.rangeCount === 0) {
        // [2, 4, 5]
        return {}; // No selection or editor not ready
    }

    const sel = s.sel;
    const range = sel.getRangeAt(0);
    const styles = {
        bold: false,
        italic: false,
        strikethrough: false,
        insertorderedlist: false,
        insertunorderedlist: false,
        blockquote: false,
    };

    if (range.collapsed) {
        // For a collapsed selection (cursor) [2, 4, 5]
        let currentNode = s.current(); // [2, 4, 5]
        let targetElement = null;
        if (currentNode) {
            targetElement = currentNode.nodeType === Node.ELEMENT_NODE ? currentNode : currentNode.parentNode;
        }

        if (targetElement instanceof HTMLElement && targetElement !== joditInstance.editor) {
            const computed = window.getComputedStyle(targetElement); // [6, 8, 9]
            const fontWeight = computed.fontWeight;
            styles.bold = fontWeight === "bold" || parseInt(fontWeight) >= 700; // [1, 10, 11]
            styles.italic = computed.fontStyle === "italic" || computed.fontStyle === "oblique"; // [12]
            styles.strikethrough = computed.textDecorationLine.includes("line-through"); // [6, 13]

            // Add checks for OL, UL, BLOCKQUOTE
            let currentCheckNode = targetElement;
            while (currentCheckNode && currentCheckNode !== joditInstance.editor) {
                if (currentCheckNode.tagName === "OL") {
                    styles.insertorderedlist = true;
                }
                if (currentCheckNode.tagName === "UL") {
                    styles.insertunorderedlist = true;
                }
                if (currentCheckNode.tagName === "BLOCKQUOTE") {
                    styles.blockquote = true;
                }
                if (styles.insertorderedlist && styles.insertunorderedlist && styles.blockquote) break; // Optimization
                currentCheckNode = currentCheckNode.parentElement;
            }
        }
    } else {
        // Ranged selection
        const commonAncestor = range.commonAncestorContainer; // [4, 7]
        if (!commonAncestor) {
            return {}; // Should not happen if range is valid
        }

        const elementsToInspect = new Set();

        // Function to add and process an element
        const processElement = (element) => {
            if (
                !(element instanceof HTMLElement) ||
                element === joditInstance.editor ||
                elementsToInspect.has(element)
            ) {
                return;
            }
            elementsToInspect.add(element);
            const computed = window.getComputedStyle(element); // [6, 8, 9]

            const fontWeight = computed.fontWeight;
            if (fontWeight === "bold" || parseInt(fontWeight) >= 700) {
                // [1, 10, 11]
                styles.bold = true;
            }
            if (computed.fontStyle === "italic" || computed.fontStyle === "oblique") {
                // [12]
                styles.italic = true;
            }

            if (computed.textDecorationLine.includes("line-through")) {
                // [6, 13]
                styles.strikethrough = true;
            }

            // Add checks for OL, UL, BLOCKQUOTE
            let currentCheckNodeForBlockElements = element;
            while (currentCheckNodeForBlockElements && currentCheckNodeForBlockElements !== joditInstance.editor) {
                if (currentCheckNodeForBlockElements.tagName === "OL") {
                    styles.insertorderedlist = true;
                }
                if (currentCheckNodeForBlockElements.tagName === "UL") {
                    styles.insertunorderedlist = true;
                }
                if (currentCheckNodeForBlockElements.tagName === "BLOCKQUOTE") {
                    styles.blockquote = true;
                }
                currentCheckNodeForBlockElements = currentCheckNodeForBlockElements.parentElement;
            }
        };

        // Process common ancestor if it's an element
        const ancestorElement =
            commonAncestor.nodeType === Node.ELEMENT_NODE ? commonAncestor : commonAncestor.parentNode;
        if (ancestorElement) processElement(ancestorElement);

        // Process parents of start and end containers
        if (range.startContainer) {
            const startParent =
                range.startContainer.nodeType === Node.ELEMENT_NODE
                    ? range.startContainer
                    : range.startContainer.parentNode;
            if (startParent) processElement(startParent);
        }
        if (range.endContainer) {
            const endParent =
                range.endContainer.nodeType === Node.ELEMENT_NODE ? range.endContainer : range.endContainer.parentNode;
            if (endParent) processElement(endParent);
        }

        // Iterate through all elements within the common ancestor that intersect the range
        if (ancestorElement instanceof HTMLElement) {
            const descendantElements = ancestorElement.getElementsByTagName("*");
            for (let i = 0; i < descendantElements.length; i++) {
                const childElement = descendantElements[i];
                // Check if the Jodit instance has the 'selection' module and then the 'range' property.
                // Ensure 'range.intersectsNode' is available and is a function before calling it.
                if (
                    joditInstance.s &&
                    joditInstance.s.range && // [2, 4, 5]
                    typeof range.intersectsNode === "function" &&
                    range.intersectsNode(childElement)
                ) {
                    processElement(childElement);
                } else if (joditInstance.s && joditInstance.s.range && !range.intersectsNode) {
                    // Fallback for environments where intersectsNode might be missing on a specific range instance
                    // This is a basic check: if child is contained by commonAncestor and commonAncestor is not too broad.
                    // A more robust intersectsNode polyfill would be better if this is a common issue.
                    if (ancestorElement.contains(childElement)) {
                        // [22]
                        processElement(childElement);
                    }
                }
            }
        }
    }

    return styles;
}

watch(
    () => props.modelValue,
    (newValue) => {
        if (joditEditor.value.value !== newValue) {
            // [info]: not sure why this setTimeout is here. removing it for now
            // setTimeout(() => {
            jodit.value = newValue;
            // }, 500);
        }
    },
);

watch(
    () => props.isDisabled,
    () => {
        nextTick(() => {
            tempInput.value.focus();
        });
    },
);

let jodit;
onMounted(async () => {
    if (props.plugins && props.plugins.length) {
        props.plugins.forEach((plugin) => {
            Jodit.plugins.add(plugin.name, plugin.callback);
        });
    }

    jodit = Jodit.make(joditEditor.value, editorConfig.value);
    await jodit.waitForReady();
    isEditorReady.value = true;
    joditEditor.value.innerHTML = props.modelValue;
    handleFocus();
    // [info]: an emits that will tell if the jodit is ready or not
    emits("joditIsReady");
    if (props.selectionEmitEnabled) {
        debounceSelectionHandler = jodit.async.debounce(() => {
            // [info]: make sure that something is selected and the focus is inside the textarea
            if (jodit.selection.html && jodit.selection.isInsideArea) {
                let selectedOptions = getJoditSelectionStyles();
                /**
                 * hasSelected an emitter that will tell if something is selected on jodit or not.
                 * selectedOptions this will return all the selected properties i.e bold, italic, strike, orderedlist, unorderedlist, backquotes
                 */
                emits("hasSelected", selectedOptions);
            } else {
                emits("hasSelected", null);
            }
        }, 250);
        const doc = jodit.o.iframe ? jodit.editorDocument : jodit.ownerDocument;
        jodit.events.on(doc, "selectionchange", debounceSelectionHandler);
    }

    jodit.events.on("change", (newValue) => {
        emits("update:modelValue", newValue);
    });

    jodit.events.on("beforeEnter", (e) => {
        if (e.key === "Enter" && e.shiftKey) {
            emits("handleShiftEnterKey", e);
            return false;
        }
    });
     jodit.value = props.modelValue;
});

const handleFocus = () => {
    nextTick(() => {
        jodit.selection.focus();
    });
};

const insertHTML = (html) => {
    if (!html) return;
    jodit.selection.insertHTML(html);
};

const setcontent = (value) => {
    handleFocus();
    jodit.selection.insertHTML(value);
};

onUnmounted(() => {
    if (debounceSelectionHandler) {
        const doc = jodit.o.iframe ? jodit.editorDocument : jodit.ownerDocument;
        jodit.events.off(doc, "selectionchange", debounceSelectionHandler);
    }
});

defineExpose({
    setcontent,
    handleFocus,
    insertHTML,
    handleStyles,
});
</script>
<style scoped>
.skeleton-animation {
    background-image: linear-gradient(90deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0));
    background-size: 40px 100%;
    background-repeat: no-repeat;
    background-position: left -40px top 0;
    -webkit-animation: shine 1s ease infinite;
    animation: shine 1s ease infinite;
}

@-webkit-keyframes shine {
    to {
        background-position: right -40px top 0;
    }
}

@keyframes shine {
    to {
        background-position: right -40px top 0;
    }
}
</style>
